import './DesktopHeader.scss';

import { b2x } from '@b2x/react/src';
import classnames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useAppContext, useAppStaticContext } from './AppContext';
import { Button } from './Button';
import { Container } from './Container';
import { HeaderMenuDesktopItemContentType } from './contentTypes';
import { t } from './i18n/i18n';
import { Icon, isIconName } from './Icon';
import { Logo } from './Logo';

export interface DesktopHeaderProps {
  activeCategory?: b2x.MenuApiDto;
  firstRowRef: React.RefObject<HTMLDivElement>;
  localeBoxVisible: boolean;
  recalculateHeaderHeight(): void;
  setActiveCategory: React.Dispatch<React.SetStateAction<b2x.MenuApiDto | undefined>>;
  toggleLocaleBoxVisible(): void;
  visibleFrom: b2x.Breakpoint;
}

export const DesktopHeader = ({
  activeCategory,
  firstRowRef,
  localeBoxVisible,
  recalculateHeaderHeight,
  setActiveCategory,
  toggleLocaleBoxVisible,
  visibleFrom,
}: DesktopHeaderProps) => {
  const { isTopBarRowVisible, transparentHeader } = useAppContext();
  const { setThingsToLoadBeforeAppReady } = b2x.useAppStaticContext();

  const menu = b2x.useMenu('MENU_HEADER_DESKTOP', { allProducts: true, populate: { content: true } });

  React.useEffect(() => {
    setThingsToLoadBeforeAppReady((prevState) => ({ ...prevState, MENU_HEADER_DESKTOP: menu }));
  }, [menu, setThingsToLoadBeforeAppReady]);

  const subMenuDropdownRef = useRef<HTMLDivElement>(null);

  const isTransparentHeader = transparentHeader && isTopBarRowVisible;

  return (
    <b2x.Sticky offset={-1}>
      <div className={classnames('desktop-header position-relative', { 'bg-black': !transparentHeader })}>
        <React.Fragment>
          <div
            className={classnames(`desktop-header-first-row py-3 d-none d-${visibleFrom}-block`, {
              'is-submenu-open': !!activeCategory || localeBoxVisible,
              transparent: isTransparentHeader,
            })}
            ref={firstRowRef}
          >
            <Container>
              <b2x.Row gap={0}>
                <b2x.Col>
                  <b2x.Div>
                    <Logo recalculateHeaderHeight={recalculateHeaderHeight} type="regular" />
                  </b2x.Div>
                </b2x.Col>
                <b2x.Col className="d-flex align-items-center justify-content-center">
                  <Menu
                    activeCategory={activeCategory}
                    menu={menu}
                    setActiveCategory={setActiveCategory}
                    subMenuDropdownRef={subMenuDropdownRef}
                  />
                </b2x.Col>
                <b2x.Col>
                  <b2x.Div alignItems={'center'} display={'flex'} justifyContent={'end'} style={{ height: '100%' }}>
                    <Toggles />
                  </b2x.Div>
                </b2x.Col>
              </b2x.Row>
            </Container>
          </div>
        </React.Fragment>

        {activeCategory && (
          <SubMenuDropdown
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            subMenuDropdownRef={subMenuDropdownRef}
          />
        )}
        {localeBoxVisible && <LocaleBox toggleLocaleBoxVisible={toggleLocaleBoxVisible} />}
      </div>
    </b2x.Sticky>
  );
};

const Toggles = () => {
  const { session } = b2x.useAppContext();

  const { showAccountOffcanvas, showCartOffcanvas } = useAppStaticContext();

  return (
    <div className="toggles">
      <div className="hstack gap-2">
        {session?.customer ? (
          <b2x.Cta
            button={{
              className: 'p-2 text-white',
              iconStart: { name: 'account-logged-in', size: 22 },
              variant: 'blank',
            }}
            code="SITE_ACCOUNT"
          />
        ) : (
          <Button
            className="p-2 text-white"
            iconEnd={{ name: 'account', size: 22 }}
            onClick={showAccountOffcanvas}
            variant="blank"
          />
        )}
        <Button className="btn-cart position-relative p-2 text-white" onClick={showCartOffcanvas} variant="blank">
          <Icon name={'cart'} size={22} />
          {session?.cart?.itemsNumber !== undefined && session.cart.itemsNumber > 0 && (
            <span className="position-absolute top-0 start-100 rounded-pill translate-middle badge bg-white text-primary">
              {session.cart.itemsNumber}
            </span>
          )}
        </Button>
      </div>
    </div>
  );
};

interface MenuItemContainerProps extends React.PropsWithChildren {
  activeCategory?: b2x.MenuApiDto;
  firstLevelCategory: b2x.MenuApiDto;
  menuItemRef?: React.RefObject<HTMLDivElement>;
}

const MenuItemContainer = ({ activeCategory, children, firstLevelCategory, menuItemRef }: MenuItemContainerProps) => {
  return (
    <div
      className={classnames('menu-item', {
        active: activeCategory && activeCategory.id === firstLevelCategory.id,
      })}
      ref={menuItemRef}
    >
      {children}
    </div>
  );
};

interface DropdownMenuButtonProps {
  activeCategory?: b2x.MenuApiDto;
  addRef(ref: React.RefObject<HTMLDivElement>): void;
  firstLevelCategory: b2x.MenuApiDto;
  pathname: string;
  removeRef(ref: React.RefObject<HTMLDivElement>): void;
  setActiveCategory: React.Dispatch<React.SetStateAction<b2x.MenuApiDto | undefined>>;
}

const DropdownMenuButton = ({
  activeCategory,
  addRef,
  firstLevelCategory,
  pathname,
  removeRef,
  setActiveCategory,
}: DropdownMenuButtonProps) => {
  const menuItemRef = useRef<HTMLDivElement>(null);
  const timeoutOnActivateCategory = useRef<NodeJS.Timeout>();

  useEffect(() => {
    addRef(menuItemRef);

    return () => removeRef(menuItemRef);
  }, [addRef, removeRef]);

  const onActivateCategory = useCallback(() => {
    timeoutOnActivateCategory.current = setTimeout(() => setActiveCategory(firstLevelCategory), 250);
  }, [setActiveCategory, firstLevelCategory]);

  const onDeactivateCategory = useCallback(() => clearTimeout(timeoutOnActivateCategory.current), []);
  useEffect(() => onDeactivateCategory, [onDeactivateCategory]);

  return (
    <MenuItemContainer
      activeCategory={activeCategory}
      firstLevelCategory={firstLevelCategory}
      menuItemRef={menuItemRef}
    >
      <b2x.router.Link
        className={classnames(
          'h-100 d-flex align-items-center h5 m-0 text-decoration-none text-white px-2 px-xl-3 fw-light letter-spacing-sm',
          firstLevelCategory.cssClass,
          { active: pathname === firstLevelCategory.link }
        )}
        onClick={onActivateCategory}
        onMouseEnter={onActivateCategory}
        onMouseLeave={onDeactivateCategory}
        to={firstLevelCategory.link}
      >
        {isIconName(firstLevelCategory.iconName) && <Icon name={firstLevelCategory.iconName} size={20} />}
        <span className="">{firstLevelCategory.label}</span>
      </b2x.router.Link>
    </MenuItemContainer>
  );
};

interface MenuProps {
  activeCategory?: b2x.MenuApiDto;
  menu?: b2x.MenuApiDto;
  setActiveCategory: React.Dispatch<React.SetStateAction<b2x.MenuApiDto | undefined>>;
  subMenuDropdownRef: React.RefObject<HTMLDivElement>;
}

const Menu = ({ activeCategory, menu, setActiveCategory, subMenuDropdownRef }: MenuProps) => {
  const idActiveCategory = activeCategory?.id;
  const onHoverOutsideCallback = useCallback(() => {
    if (idActiveCategory) {
      setActiveCategory(undefined);
    }
  }, [idActiveCategory, setActiveCategory]);

  const { addRef, removeRef } = b2x.useOnHoverOutside(onHoverOutsideCallback, [subMenuDropdownRef], 300);
  const { pathname } = b2x.router.useLocation();

  return (
    <div className="menu h-100 d-flex">
      {menu?.children.map(
        (firstLevelCategory) =>
          (firstLevelCategory.children.length > 0 || firstLevelCategory.link !== undefined) &&
          (firstLevelCategory.children.length > 0 ? (
            <DropdownMenuButton
              activeCategory={activeCategory}
              addRef={addRef}
              firstLevelCategory={firstLevelCategory}
              key={firstLevelCategory.id}
              pathname={pathname}
              removeRef={removeRef}
              setActiveCategory={setActiveCategory}
            />
          ) : (
            <MenuItemContainer
              activeCategory={activeCategory}
              firstLevelCategory={firstLevelCategory}
              key={firstLevelCategory.id}
            >
              <b2x.router.Link
                className={classnames(
                  'h-100 d-flex align-items-center h5 m-0 text-decoration-none text-white px-2 px-xl-3 letter-spacing-sm text-nowrap',
                  {
                    active: pathname === firstLevelCategory.link,
                  }
                )}
                to={firstLevelCategory.link}
              >
                {firstLevelCategory.label}
              </b2x.router.Link>
            </MenuItemContainer>
          ))
      )}
    </div>
  );
};

interface SubMenuDropdownProps {
  activeCategory: b2x.MenuApiDto;
  setActiveCategory: React.Dispatch<React.SetStateAction<b2x.MenuApiDto | undefined>>;
  subMenuDropdownRef: React.RefObject<HTMLDivElement>;
}

const SubMenuDropdown = ({ activeCategory, setActiveCategory, subMenuDropdownRef }: SubMenuDropdownProps) => {
  const close = React.useCallback(() => {
    setActiveCategory(undefined);
  }, [setActiveCategory]);

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setIsMounted(true), 50);
    return () => clearTimeout(timeout);
  }, []);

  b2x.useKeyPress('Escape', close);

  return (
    <b2x.Div
      className={classnames({ mounted: isMounted }, 'sub-menu-dropdown position-absolute w-100 shadow-sm text-white')}
      innerRef={subMenuDropdownRef}
      paddingY={5}
    >
      <Container>
        <b2x.Row gap={3} justifyContent={'center'}>
          {activeCategory.children.map((secondLevelCategory) => (
            <b2x.Col key={secondLevelCategory.id} size={2}>
              <RecursiveMenuItem close={close} item={secondLevelCategory} />
            </b2x.Col>
          ))}
        </b2x.Row>
      </Container>
    </b2x.Div>
  );
};

interface RecursiveMenuItemProps {
  close?(): void;
  deep?: number;
  item?: b2x.MenuApiDto;
}

const RecursiveMenuItem = ({ close, deep = 1, item }: RecursiveMenuItemProps) => {
  return (
    <div className={classnames(`deep-${deep}`, item?.code)}>
      {item?.children && item.children.length > 0 ? (
        item.children.map((child) => <RecursiveMenuItem close={close} deep={deep + 1} item={child} key={child.id} />)
      ) : (
        <>
          {item?.content && item.content.type === 'HEADER_MENU_DESKTOP_ITEM_CONTENT_TYPE'
            ? b2x.typedContent<HeaderMenuDesktopItemContentType>(item.content, (content) => (
                <b2x.Div>Contenuto</b2x.Div>
              ))
            : item && (
                <b2x.Div>
                  <b2x.ConditionalWrapper
                    condition={item.link !== undefined}
                    wrapper={<b2x.router.Link className="text-decoration-none" onClick={close} to={item.link} />}
                  >
                    {item.image && (
                      <b2x.Div marginBottom={3}>
                        <b2x.Image {...item.image} fluid />
                      </b2x.Div>
                    )}
                    {item.label && (
                      <b2x.P className="text-white" fontWeight={700} marginBottom={0}>
                        {item.label}
                      </b2x.P>
                    )}

                    {item.subTitle && (
                      <b2x.P
                        className="text-white text-uppercase extra-small"
                        marginBottom={0}
                        style={{ fontWeight: 300 }}
                      >
                        {item.subTitle}
                      </b2x.P>
                    )}
                  </b2x.ConditionalWrapper>
                  {item.children.length > 0 && (
                    <ul className="list-unstyled m-0">
                      {item.children.map((child) => (
                        <li key={child.id}>
                          <b2x.router.Link className="small text-decoration-none" onClick={close} to={child.link}>
                            {child.label}
                          </b2x.router.Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </b2x.Div>
              )}
        </>
      )}
    </div>
  );
};

interface LocaleBoxProps {
  toggleLocaleBoxVisible(): void;
}

const LocaleBox = ({ toggleLocaleBoxVisible }: LocaleBoxProps) => {
  const { info } = b2x.useAppContext();

  return (
    <Box className="text-white locale-box" onCloseButtonClick={toggleLocaleBoxVisible}>
      <Container>
        <b2x.Row justifyContent={'center'}>
          <b2x.Col size={{ lg: 6, xl: 5 }}>
            <b2x.LocaleForm className="mb-3" />
            {info?.shippingCountries?.length && info.shippingCountries.length > 1 && (
              <div className="text-center">{t('misc.changeCountryMessage')}</div>
            )}
          </b2x.Col>
        </b2x.Row>
      </Container>
    </Box>
  );
};

interface BoxProps {
  children?: React.ReactNode;
  className?: string;
  onCloseButtonClick(): void;
}

const Box = ({ children, className, onCloseButtonClick }: BoxProps) => {
  b2x.useKeyPress('Escape', onCloseButtonClick);

  const ref = React.useRef<HTMLDivElement>(null);

  const handleOutsideClick = React.useCallback(() => {
    onCloseButtonClick();
  }, [onCloseButtonClick]);

  b2x.useOutsideClickHandler(ref, handleOutsideClick);

  return (
    <div className={classnames(className, 'box position-absolute w-100 shadow-sm py-4')} ref={ref}>
      <Container>
        <div className="d-flex justify-content-end">
          <Button
            className="p-2 text-white"
            iconEnd={{ name: 'close', size: 20 }}
            onClick={onCloseButtonClick}
            variant="blank"
          />
        </div>
      </Container>
      {children}
    </div>
  );
};
